import { Injectable, inject, signal, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Navigation screen definition
 */
export interface NavigationScreen {
  route: string;
  label: string;
}

/**
 * Swipe Navigation Service
 *
 * Enables horizontal swipe gestures to navigate between main screens.
 * Works like Instagram/TikTok style navigation between tabs.
 * Triggers navigation early on confident swipe for immediate visual feedback.
 */
@Injectable({
  providedIn: 'root',
})
export class SwipeNavigationService {
  private readonly router = inject(Router);

  /**
   * List of navigable screens in order (left to right)
   */
  private readonly screens: NavigationScreen[] = [
    { route: '/offers', label: 'Ofertas' },
    { route: '/menus', label: 'Menús' },
    { route: '/places', label: 'Mapa' },
    { route: '/liked-offers', label: 'Favoritos' },
  ];

  /**
   * Current screen index
   */
  private readonly currentIndex = signal<number>(0);

  /**
   * Whether swipe navigation is enabled
   */
  readonly enabled = signal<boolean>(true);

  /**
   * Whether a swipe navigation was just triggered (for route animation)
   */
  readonly isSwiping = signal<boolean>(false);

  /**
   * Current swipe direction (-1 = left/next, 0 = none, 1 = right/previous)
   */
  readonly swipeDirection = signal<-1 | 0 | 1>(0);

  /**
   * Whether navigation has been triggered for the current gesture
   * Prevents multiple navigations during a single swipe
   */
  private navigationTriggered = false;

  /**
   * Get current screen info
   */
  readonly currentScreen = computed(() => {
    const index = this.currentIndex();
    return this.screens[index] || null;
  });

  /**
   * Check if can navigate to previous screen
   */
  readonly canNavigatePrevious = computed(() => this.currentIndex() > 0);

  /**
   * Check if can navigate to next screen
   */
  readonly canNavigateNext = computed(
    () => this.currentIndex() < this.screens.length - 1,
  );

  constructor() {
    // Listen to route changes to update current index
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateCurrentIndex(event.url);
      });
  }

  /**
   * Update current index based on URL
   */
  private updateCurrentIndex(url: string): void {
    // Remove query params for matching
    const basePath = url.split('?')[0];

    const index = this.screens.findIndex(
      (screen) =>
        basePath === screen.route || basePath.startsWith(screen.route + '/'),
    );

    if (index !== -1) {
      this.currentIndex.set(index);
    }
  }

  /**
   * Check if current route is a primary navigable screen (exact match only).
   * Swipe navigation is disabled on detail pages and sub-routes.
   */
  isNavigableScreen(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    // Only allow swipe on exact primary screen matches
    return this.screens.some((screen) => currentUrl === screen.route);
  }

  /**
   * Navigate to previous screen (swipe right)
   */
  navigatePrevious(): boolean {
    if (!this.enabled() || !this.canNavigatePrevious()) {
      return false;
    }

    const newIndex = this.currentIndex() - 1;
    const targetScreen = this.screens[newIndex];

    if (targetScreen) {
      this.swipeDirection.set(1); // Swiping right (going to previous)
      this.router.navigate([targetScreen.route]);
      return true;
    }

    return false;
  }

  /**
   * Navigate to next screen (swipe left)
   */
  navigateNext(): boolean {
    if (!this.enabled() || !this.canNavigateNext()) {
      return false;
    }

    const newIndex = this.currentIndex() + 1;
    const targetScreen = this.screens[newIndex];

    if (targetScreen) {
      this.swipeDirection.set(-1); // Swiping left (going to next)
      this.router.navigate([targetScreen.route]);
      return true;
    }

    return false;
  }

  /**
   * Navigate to a specific screen by index
   */
  navigateToIndex(index: number): boolean {
    if (!this.enabled() || index < 0 || index >= this.screens.length) {
      return false;
    }

    const targetScreen = this.screens[index];
    if (targetScreen) {
      const currentIdx = this.currentIndex();
      this.swipeDirection.set(index > currentIdx ? -1 : 1);
      this.router.navigate([targetScreen.route]);
      return true;
    }

    return false;
  }

  /**
   * Get all screens
   */
  getScreens(): NavigationScreen[] {
    return [...this.screens];
  }

  /**
   * Get current index
   */
  getCurrentIndex(): number {
    return this.currentIndex();
  }

  /**
   * Enable/disable swipe navigation
   */
  setEnabled(enabled: boolean): void {
    this.enabled.set(enabled);
  }

  /**
   * Reset swipe direction
   */
  resetSwipeDirection(): void {
    this.swipeDirection.set(0);
  }

  /**
   * Start tracking a new swipe gesture
   */
  startSwipeGesture(): void {
    this.navigationTriggered = false;
  }

  /**
   * Try to trigger navigation during swipe gesture based on distance and velocity.
   * Uses a fluid approach - fast swipes trigger with less distance, slow drags need more.
   *
   * @param deltaX Horizontal movement (positive = right/previous, negative = left/next)
   * @param velocity Current swipe velocity in pixels/ms
   * @param distanceThreshold Base distance threshold (default 80px)
   * @param velocityThreshold Velocity threshold for fast swipes (default 0.5 px/ms)
   * @returns Whether navigation was triggered
   */
  tryFluidNavigation(
    deltaX: number,
    velocity: number,
    distanceThreshold = 80,
    velocityThreshold = 0.5,
  ): boolean {
    // Don't trigger multiple times per gesture
    if (this.navigationTriggered) {
      return false;
    }

    const absDelta = Math.abs(deltaX);
    const absVelocity = Math.abs(velocity);

    // Determine if we should trigger navigation:
    // 1. Fast swipe (high velocity) - trigger earlier with less distance (minimum 30px)
    // 2. Slow drag - need full distance threshold
    const isFastSwipe = absVelocity >= velocityThreshold && absDelta >= 30;
    const isFullDistance = absDelta >= distanceThreshold;

    if (!isFastSwipe && !isFullDistance) {
      return false;
    }

    // Trigger navigation
    this.navigationTriggered = true;
    this.isSwiping.set(true);

    if (deltaX > 0 && this.canNavigatePrevious()) {
      return this.navigatePrevious();
    } else if (deltaX < 0 && this.canNavigateNext()) {
      return this.navigateNext();
    }

    this.navigationTriggered = false;
    this.isSwiping.set(false);
    return false;
  }

  /**
   * End the swipe gesture tracking
   */
  endSwipeGesture(): void {
    // Reset after a short delay to allow animation to complete
    setTimeout(() => {
      this.isSwiping.set(false);
    }, 300);
  }
}
